import angular from 'angular';
import * as IpSubnetCalculator from 'ip-subnet-calculator';
import _ from 'lodash';
import moment from 'moment-timezone';

import {
    EpsonAlign,
    EpsonBarcode,
    EpsonColor,
    EpsonFont,
    EpsonHRI,
    EpsonSize,
    NFSendResult,
    NFThermalPrinter,
    NFThermalPrinterConstructor
} from 'src/app/shared/model/nf-thermal-printer.model';

import {
    ConfigurationManagerService,
    EntityManagerService,
    EnvironmentInfoService,
    ErrorsLoggerService
} from 'src/app/core';

import {
    Categories,
    Printers,
    Sales,
    SalesDocuments,
    SalesItems,
    SalesItemsIngredients
} from 'tilby-models';

import {
    asyncParallelLimit,
    groupBy,
    keyBy,
} from 'src/app/shared/utils';

import {
    VariationSale,
    VariationSaleItem
} from 'src/app/shared/model/cashregister.model';

import { TranslateService } from '@ngx-translate/core';
import { DocumentPrinterOptions } from 'src/app/shared/model/document-printer.model';

import {
    DocumentBuilderService,
    OrderPrinterConfiguration,
    PrinterUtilsService,
    OrderPrinterWithConfig,
    SaleUtilsService,
    DocumentPrinterWithConfig
} from 'src/app/features';

import {
    EPosDriver,
    EscPosDriver,
    NFHtmlDriver,
    NFPrintCallbackData,
    NFThermalPrinterConfig,
    PrintLayoutBuilder,
} from 'src/app/features/printers/drivers';

import { NonFiscalPreviewDialogService } from 'src/app/dialogs';

import html2pdf from 'html2pdf.js';

import {
    CustomerUtilsService
} from 'src/app/features/customers/services/customer-utils.service';

import {
    truncateString
} from 'src/app/shared/string-utils';

type DriverInfo = {
    driverObj: NFThermalPrinterConstructor;
    port: number;
    configuration: NFThermalPrinterConfig
};

type SetupOptions = {
    goExit_skip_content?: boolean;
}

type PrintFreeOptions = {
    printLogo: boolean
    printCopyHeader: boolean
    header: string[]
    barcode: {
        type: 'CODE39' | 'EAN13' | 'QRCODE'
        value: string
    }
};

type SaleWithOptions = Sales & {
    options?: PrintNonFiscalSaleOptions,
    print_type: 'sale_nonfiscal'
};

type VariationSaleItemWithPrice = VariationSaleItem & {
    final_price?: number,
    category_id?: number,
    category_name?: string
    printers?: Record<number, boolean>
}

type VariationSaleWithPrices = Omit<VariationSale, 'variation_items'> & {
    variation_items: VariationSaleItemWithPrice[],
    final_amount?: number
}

type PrintInfo = {
    print_type: 'order'
    printed_at?: string
    goExit?: boolean
    reprint?: boolean
    reprint_info?: {
        reprint_source: string
        reprint_new: string
    }
    replacement_info?: {
        is_replacement: boolean;
        changed_uuids: Set<string>;
    }
}

export type TableVariation = {
    print_type: 'table_variation'
    previous: Pick<Sales, 'table_id' | 'room_id' | 'table_name' | 'room_name'>
    current: Pick<Sales, 'table_id' | 'room_id' | 'table_name' | 'room_name'>
}

type SaleItemWithInfo = SalesItems & {
    printers?: Record<number, boolean>,
    order_name?: string
}

type PrintableVariation = VariationSaleWithPrices & PrintInfo;

type PrintableSale = Omit<Sales, 'sale_items'> & {
    sale_items: SaleItemWithInfo[]
} & PrintInfo;

type PrintableSaleOrVariation = PrintableVariation | PrintableSale;

type PrintItemGroupConfig = {
    bold?: boolean,
    itemFontSize: number,
    variationFontSize: number,
    padding: number
}

type ItemGroupData = {
    name: string;
    is_menu_item: boolean;
    ingredients: string[];
    variations: string[];
    quantity: number;
    notes: string;
    uuid: string;
};

type ItemVariationGroupData = ItemGroupData & {
    quantityDifference: boolean
    underLineIngredients: boolean
    underlineVariations: boolean
    underlineNotes: boolean
}

type PrintNonFiscalSaleOptions = {
    header?: string[]
    tail?: string
    tailQRCode?: string
    tailQRCodeSize?: number
}

type NonFiscalLabel =
    'added' |
    'covers' |
    'current_table' |
    'customer_delivery' |
    'customer_notes' |
    'customer' |
    'customer_stub' |
    'deliver_at_date' |
    'deliver_at' |
    'delivery' |
    'exit' |
    'fidelity' |
    'final_amount' |
    'go_exit_name' |
    'go_exit' |
    'item_addition' |
    'item_edit' |
    'item_editquantity' |
    'item_removal' |
    'mobile' |
    'no_table' |
    'notes' |
    'of' |
    'order_notes' |
    'order' |
    'phone' |
    'previous_table' |
    'removed' |
    'replacement' |
    'reprint_new' |
    'reprint_order' |
    'reprint_source' |
    'reprinted_at' |
    'table_edit' |
    'table' |
    'take_away' |
    'total_pieces' |
    'variation' |
    'waiter'

export class NonFiscalDriver {
    static $inject = [
        "$translate",
        "$http",
        "$filter",
        "$injector",
        "environmentInfo",
        "errorsLogger",
        "DocumentBuilder",
        "saleUtils",
        "entityManager",
        "checkManager",
        'nonFiscalPreviewDialog',
        'printerUtils',
    ];

    constructor(
        private readonly $translate: TranslateService,
        private readonly $http: any,
        private readonly $filter: any,
        private readonly $injector: any,
        private readonly environmentInfoService: EnvironmentInfoService,
        private readonly errorsLogger: ErrorsLoggerService,
        private readonly DocumentBuilder: DocumentBuilderService,
        private readonly saleUtilsService: SaleUtilsService,
        private readonly entityManagerService: EntityManagerService,
        private readonly configurationManagerService: ConfigurationManagerService,
        private readonly nonFiscalPreviewDialog: NonFiscalPreviewDialogService,
        private readonly printerUtilsService: PrinterUtilsService,
    ) {
    }

    private printers: OrderPrinterWithConfig[] = [];

    private configuration = {
        columns: 48,
        top_space: 0,
        bottom_space: 3,
        goExit_skip_content: false
    };

    private newLine(times = 1) {
        return '\n\r'.repeat(times);
    }

    private border(length?: number, char: string = '=') {
        length = Math.floor((length || this.configuration.columns) / 2);

        return char.repeat(length) + this.newLine();
    }


    private validateIPaddress(ipaddress: string) {
        return (/^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(ipaddress));
    }

    private intToIP(int: number) {
        return [((int >> 24) & 255), ((int >> 16) & 255), ((int >> 8) & 255), (int & 255)].join('.');
    }

    private logEvent(...args: any) {
        this.errorsLogger.debug('[ NonFiscalDriver ]', ...args);
    }

    private async showPrintPreview(data: NFPrintCallbackData, printer: OrderPrinterWithConfig) {
        if (!data.documents) {
            return;
        }

        for (const doc of data.documents) {
            const confirm = await this.nonFiscalPreviewDialog.openDialog({
                data: {
                    htmlContent: doc,
                    printerName: printer.name
                }
            });

            if (!confirm) {
                throw undefined;
            }
        }
    }

    private async printImage(data: NFPrintCallbackData, printer: OrderPrinterWithConfig) {
        if (!data.documents) {
            return;
        }

        const width = 488;

        const imageConfig = {
            width: width,
            scale: 2,
            logging: false,
            ignoreElements: (el: any) => (el.nodeName === 'HEAD' || el.id === 'content')
        }

        const printerConfig: OrderPrinterConfiguration = {
            ...printer.configuration,
            codepage_mapping: 'epson'
        };

        const driverInfo = this.getDriverInfo({
            ...printer,
            configuration: printerConfig
        });

        if (!driverInfo) {
            return;
        }

        const driverObj = new driverInfo.driverObj(printer.ip_address!, driverInfo.port, driverInfo.configuration);

        if (!driverObj.addImage) {
            return;
        }

        for (const doc of data.documents) {
            const img = await html2pdf().set({ html2canvas: imageConfig }).from(doc).outputImg('datauri');

            await driverObj.addImage(img);
            driverObj.addCut();
        }

        await driverObj.send().then((res) => {
            if (!['PRINTED', 'EMPTY_ORDER'].includes(res)) {
                throw res;
            }
        });
    }

    private getDriverInfo(printer: OrderPrinterWithConfig): DriverInfo | undefined {
        if (this.configurationManagerService.getShopPreference('nofiscalprinter.enable_virtual_printer') && printer.ip_address === '0.0.0.0') {
            return {
                driverObj: NFHtmlDriver,
                port: 0,
                configuration: {
                    columns: printer.columns,
                    lineSpacing: printer.configuration.line_spacing,
                    printCallback: (data) => this.showPrintPreview(data, printer),
                }
            };
        }

        if (printer.configuration.codepage_mapping === 'raster') {
            return {
                driverObj: NFHtmlDriver,
                port: 0,
                configuration: {
                    lineSpacing: printer.configuration.line_spacing,
                    printCallback: (data) => this.printImage(data, printer),
                    splitDocumentsOnCut: true
                }
            };
        }

        let printerDriver: NFThermalPrinterConstructor | undefined;
        let port: number | undefined;
        let config: DriverInfo['configuration'] = {};

        switch (printer.driver) {
            case 'escpos':
                printerDriver = EscPosDriver;
                port = 9100;
                config = {
                    codepageMapping: printer.configuration.codepage_mapping,
                    //@ts-ignore
                    connectionType: printer.connection_type,
                    lineSpacing: printer.configuration.line_spacing
                };
                break;
            case 'epos':
                printerDriver = EPosDriver;
                port = 80;
                config = {
                    lineSpacing: printer.configuration.line_spacing
                };
                break;
        }

        return printerDriver ? { driverObj: printerDriver, port: port!, configuration: config } : undefined;
    }

    private getNonFiscalLabel(label: NonFiscalLabel, params?: Record<string, string>) {
        return this.$translate.instant(`PRINTERS.NON_FISCAL_LABELS.${label.toUpperCase()}`, params);
    }


    private prepareItemGroups<T extends SaleItemWithInfo | VariationSaleItemWithPrice>(ownItems: T[], otherInlineItems: T[] | undefined, itemsGroupBy: OrderPrinterWithConfig['configuration']['items_group_by']) {
        const grouper = this.groupItemsByGroupByPreference(itemsGroupBy);
        const groupedOwn = groupBy(ownItems, grouper);
        const groupedOther = groupBy(otherInlineItems || [], grouper);

        const sortedGroupKeys = Array.from(new Set([
            ...Object.keys(groupedOwn),
            ...Object.keys(groupedOther)
        ])).map(Number).sort((a, b) => a - b);

        return {
            groupedOwn,
            groupedOther,
            sortedGroupKeys,
            lastGroupIndex: sortedGroupKeys.length - 1
        };
    }

    private groupItemsByGroupByPreference(itemsGroupBy: OrderPrinterWithConfig['configuration']['items_group_by']): (item: SaleItemWithInfo | VariationSaleItemWithPrice) => number | undefined {
        switch (itemsGroupBy) {
            case 'category_id':
                return (item) => item.category_id || 0;
            case 'exit':
            case 'exit_and_category':
            default:
                return (item) => item.exit || 0;
        }
    }

    private groupItemsByPrinterOwnership<T extends SaleItemWithInfo | VariationSaleItemWithPrice>(items: T[], printer: OrderPrinterWithConfig): { own?: T[], other_inline?: T[], other?: T[], discard?: T[] } {
        const targetPrinterId = printer.id;
        const otherPrinters = printer.configuration?.other_printers || [];

        // Create a map for O(1) lookup to check if a printer belongs to "other"
        const otherPrintersMap = new Map<number, boolean>();

        for (const p of otherPrinters) {
            if (p.id) {
                otherPrintersMap.set(Number(p.id), !!p.inline);
            }
        }

        return groupBy(items, (item) => {
            const itemPrinters = item.printers;

            if (!itemPrinters) {
                return 'discard';
            }

            // Check if the item belongs to the main printer
            if (targetPrinterId && itemPrinters[targetPrinterId]) {
                return 'own';
            }

            // Search for a match in secondary printers by iterating through item printer keys
            for (const printerId in itemPrinters) {
                if (otherPrintersMap.has(Number(printerId))) {
                    return otherPrintersMap.get(Number(printerId))
                        ? 'other_inline'
                        : 'other';
                }
            }

            return 'discard';
        });
    }

    private isPrintersRoomOrder(printer: OrderPrinterWithConfig, sale: PrintableSaleOrVariation) {
        const allowedRooms = new Set(printer.configuration.rooms);

        // if no rooms are allowed, allow all
        if (!allowedRooms.size) {
            return true;
        }

        if (sale.room_id) {
            if (allowedRooms.has(sale.room_id)) {
                return true;
            }
        } else {
            if ((sale.order_type === 'normal' || !sale.order_type) && allowedRooms.has('no_rooms')) {
                return true;
            }
            if (sale.order_type === 'take_away' && allowedRooms.has('take_away')) {
                return true;
            }
            if (sale.order_type === 'delivery' && allowedRooms.has('delivery')) {
                return true;
            }
        }

        return false;
    }

    private buildShopHeader(builder: PrintLayoutBuilder, header: string[]) {
        for (const [idx, line] of header.entries()) {
            builder.addTextLine(line, {
                align: EpsonAlign.CENTER,
                size: idx ? 0 : EpsonSize.DOUBLE_HEIGHT
            });
        }

        builder.newLine();
    };

    private settingsToCheck = [
        'order_number',
        'operator_name',
        'print_type',
        'date',
        'time',
        'room_name',
        'table_name',
        'covers',
        'delivery_info',
        'order_name',
        'customer_info',
        'order_notes',
        'order_categories',
        'total_pieces',
        'final_amount'
    ];

    private getLayoutConfig(printer: OrderPrinterWithConfig) {
        return {
            itemFontSize: printer.configuration.item_font_size ?? 17,
            itemSpacing: printer.configuration.item_spacing ?? 1,
            itemHideBorder: printer.configuration.item_hide_border ?? false,
            itemHideBrackets: printer.configuration.item_hide_brackets ?? false,
            itemsGroupBy: printer.configuration.items_group_by ?? 'exit',
            splitOrders: printer.configuration.split_orders_by_exit ?? false,
            addPrices: printer.configuration.add_prices_to_order ?? false,
            mergeSameItems: printer.configuration.merge_same_items ?? false,
            variationFontSize: printer.configuration.variation_font_size ?? 16,
            otherPrintersFontSize: printer.configuration.other_printers_font_size ?? 0,
            itemsAlign: printer.configuration.items_align ?? EpsonAlign.CENTER,
            topSpace: this.newLine().repeat(printer.top_space ?? this.configuration.top_space),
            bottomSpace: this.newLine().repeat(printer.bottom_space ?? this.configuration.bottom_space),
        };
    }

    private getHeaderFooterSettings(printer: OrderPrinterWithConfig, settingsType: 'header' | 'footer') {
        const settings: any = {
            columns: printer.columns,
            settings_type: settingsType
        };

        const printerConfig = printer.configuration;

        //Initialize settings
        switch (settingsType) {
            case 'header':
                for (const settingName of this.settingsToCheck) {
                    const finalName = `header_${settingName}` as keyof OrderPrinterWithConfig['configuration'];

                    if (['total_pieces', 'final_amount'].includes(settingName)) {
                        settings[settingName] = printerConfig[finalName] != null ? printerConfig[finalName] : -1;
                    } else {
                        settings[settingName] = printerConfig[finalName] != null ? printerConfig[finalName] : ['header_order_name', 'header_order_notes'].includes(finalName) ? 0 : 16;
                    }
                }
                break;
            case 'footer':
                for (const settingName of this.settingsToCheck) {
                    const finalName = `footer_${settingName}` as keyof OrderPrinterWithConfig['configuration'];

                    settings[settingName] = printerConfig[finalName] != null ? printerConfig[finalName] : -1;
                }
                break;
            default: break;
        }

        return settings;
    };

    private buildOrderHeader(builder: PrintLayoutBuilder, printer: OrderPrinterWithConfig, sale: PrintableSaleOrVariation, type: 'header' | 'footer') {
        const printerSettings = this.getHeaderFooterSettings(printer, type);
        const printDate = moment(sale.printed_at);

        type HeaderRow = {
            text: string;
            size?: number;
            align?: EpsonAlign;
            bold?: boolean;
            reversed?: boolean;
        };

        const headerData: HeaderRow[] = [];

        if (printerSettings.order_categories !== -1) {
            headerData.push({
                text: (printer.categories || []).map(c => c.name).join(', ') + this.newLine(2),
                size: printerSettings.order_categories,
                align: EpsonAlign.CENTER,
                bold: true
            });
        }

        if (printerSettings.order_number !== -1) {
            const orderNumberString = sale.sale_number ? `#${sale.sale_number}` : '';

            if (orderNumberString) {
                headerData.push({
                    text: orderNumberString,
                    size: printerSettings.order_number
                });
            }
        }

        if (printerSettings.operator_name !== -1) {
            if (sale.seller_name) {
                let opName = sale.seller_name;

                if (printerSettings.operator_name > 0) {
                    opName += this.newLine();
                }

                headerData.push({
                    text: opName,
                    size: printerSettings.operator_name
                });
            }
        }

        if (printerSettings.date !== -1) {
            let headerDate = printDate.format('L');

            if (printerSettings.seller_name === 0) {
                headerDate += this.newLine();
            }

            headerData.push({ text: headerDate, size: printerSettings.date });
        }

        if (printerSettings.time !== -1) {
            headerData.push({ text: printDate.format('LT'), size: printerSettings.time });
        }

        for (const row of headerData) {
            if (!row.text.endsWith(this.newLine()) && row !== headerData[headerData.length - 1]) {
                row.text += ' - ';
            }
        }

        if (headerData.length) {
            headerData.push({
                text: this.newLine(2)
            });
        }

        if (printerSettings.print_type !== -1) {
            if ('replacement_info' in sale && sale.replacement_info?.is_replacement) {
                headerData.push({
                    text: ` ${this.getNonFiscalLabel('replacement')} ${this.newLine(2)}`,
                    size: printerSettings.print_type,
                    bold: true,
                    reversed: true
                });
            } else if ('variation_items' in sale) { // If order variation print
                headerData.push({
                    text: ` ${this.getNonFiscalLabel('variation')} ${this.newLine(2)}`,
                    size: printerSettings.print_type,
                    bold: true,
                    reversed: true
                });
            } else if ('reprint' in sale && sale.reprint) { // if reprint
                headerData.push({
                    text: ` ${this.getNonFiscalLabel('reprint_order')} ${this.newLine(2)}`,
                    size: printerSettings.print_type,
                    bold: true,
                    reversed: true
                });
            }
        }

        switch (sale.order_type) {
            case 'normal':
                if (sale.table_name && printerSettings.room_name !== -1) {
                    headerData.push({
                        text: sale.room_name + this.newLine(),
                        size: printerSettings.room_name
                    });
                }

                if (sale.table_name && printerSettings.table_name !== -1) {
                    headerData.push({
                        text: sale.table_name + this.newLine(),
                        size: printerSettings.table_name
                    });
                }

                if (sale.covers && printerSettings.covers !== -1) {
                    headerData.push({
                        text: `${sale.covers} ${this.getNonFiscalLabel('covers')}` + this.newLine(),
                        size: printerSettings.covers
                    });
                }
                break;
            case 'take_away':
            case 'delivery':
                if (printerSettings.delivery_info !== -1) {
                    let deliveryInfo = this.getNonFiscalLabel(sale.order_type) + this.newLine();

                    if ('channel' in sale) {
                        if ((sale.channel && sale.channel !== 'pos') || sale.external_id) {
                            deliveryInfo += (sale.channel ? sale.channel + ' ' : '') + (sale.external_id ? `#${sale.external_id}` : '') + this.newLine();
                        }
                    }

                    const deliverAt = moment(sale.deliver_at);

                    if (deliverAt.dayOfYear() === printDate.dayOfYear() && deliverAt.year() === printDate.year()) { //Same day
                        deliveryInfo += `${this.getNonFiscalLabel('deliver_at')} ${deliverAt.format('LT')}` + this.newLine(2);
                    } else { //Different day
                        deliveryInfo += `${this.getNonFiscalLabel('deliver_at_date')} ${deliverAt.format('L LT')}` + this.newLine(2);
                    }

                    headerData.push({
                        text: deliveryInfo,
                        size: printerSettings.delivery_info
                    });
                }
                break;
            default:
                break;
        }

        for (const row of headerData) {
            Object.assign(row, {
                bold: true,
                align: EpsonAlign.CENTER
            });
        }

        if (printerSettings.order_name !== -1) {
            headerData.push({
                text: sale.name + this.newLine(2),
                size: printerSettings.order_name,
                align: EpsonAlign.CENTER
            });
        }

        const saleCustomer = sale.sale_customer;
        const customerData: HeaderRow[] = [];

        // Add customer (if exists)
        if (saleCustomer && printerSettings.customer_info !== -1) {
            customerData.push({
                text: (sale.order_type === 'delivery' ? this.getNonFiscalLabel('customer_delivery')! : this.getNonFiscalLabel('customer')!)
            }, {
                text: ` ${CustomerUtilsService.getCustomerName(saleCustomer)}`,
                bold: true
            });


            if (saleCustomer.fidelity) {
                customerData.push({
                    text: `  ${this.getNonFiscalLabel('fidelity')} ${saleCustomer.fidelity}`
                });
            }

            if (saleCustomer.shipping_street) {
                customerData.push({
                    text: `  ${saleCustomer.shipping_street} ${saleCustomer.shipping_number || ''}`
                });

                customerData.push({
                    text: `  ${saleCustomer.shipping_zip ? `${saleCustomer.shipping_zip} ` : ''}${saleCustomer.shipping_city ? `${saleCustomer.shipping_city} ` : ''}${saleCustomer.shipping_prov ? `(${saleCustomer.shipping_prov})` : ''}`
                });

            }

            if (saleCustomer.phone) {
                customerData.push({
                    text: `  ${this.getNonFiscalLabel('phone')} ${saleCustomer.phone}`
                });
            }

            if (saleCustomer.mobile) {
                customerData.push({
                    text: `  ${this.getNonFiscalLabel('mobile')} ${saleCustomer.mobile}`
                });
            }

            if (saleCustomer.notes) {
                customerData.push({
                    text: `  ${this.getNonFiscalLabel('customer_notes')} ${saleCustomer.notes.toUpperCase()}`
                });
            }

            for (const row of customerData) {
                Object.assign(row, {
                    text: row.text + this.newLine(),
                    size: printerSettings.customer_info
                });
            }

            headerData.push(...customerData);
        }

        if (sale.notes && printerSettings.order_notes !== -1) {
            headerData.push({
                text: this.border(printerSettings.columns, '='),
                size: EpsonSize.DOUBLE_WIDTH
            }, {
                text: `${this.getNonFiscalLabel('order_notes')} ${sale.notes}`,
                size: printerSettings.order_notes
            });
        }

        if (printerSettings.total_pieces !== -1) {
            let totalQuantity = 0;

            if ('variation_items' in sale) {
                // Calculate total quantity considering only additions
                totalQuantity = sale.variation_items!.filter((item) => item.quantity_difference! >= 0).reduce((total, item) => total + (item.quantity_difference || item.quantity || 0), 0);
            } else if ('sale_items' in sale) {
                // Calculate total quantity considering the sum of the quantities of the whole order
                totalQuantity = sale.sale_items!.reduce((total, item) => total + (item.quantity || 0), 0);
            }

            headerData.push({
                text: this.border(printerSettings.columns, '='),
                size: EpsonSize.DOUBLE_WIDTH
            }, {
                text: `${this.getNonFiscalLabel('total_pieces')} ${totalQuantity}` + this.newLine(),
                size: printerSettings.total_pieces
            });
        }

        if (printerSettings.final_amount !== -1) {
            // Add separator if we didn't add it before in total_pieces
            if (printerSettings.total_pieces === -1) {
                headerData.push({
                    text: this.border(printerSettings.columns, '='),
                    size: EpsonSize.DOUBLE_WIDTH
                });
            }

            headerData.push({
                text: `${this.getNonFiscalLabel('final_amount')} ${this.$filter('sclCurrency')(sale.final_amount || 0)}` + this.newLine(),
                size: printerSettings.final_amount
            });
        }

        if (headerData.length) {
            headerData.unshift({
                text: this.border(printerSettings.columns, '='),
                size: EpsonSize.DOUBLE_WIDTH
            });
        }

        headerData.push({
            text: this.border(printerSettings.columns, '='),
            size: EpsonSize.DOUBLE_WIDTH
        });

        if (type === 'footer' && sale.reprint_info) {
            headerData.push({
                text: this.newLine(),
                size: EpsonSize.DOUBLE_WIDTH
            }, {
                text: `${this.getNonFiscalLabel('reprinted_at')} ${moment().format('HH:mm DD/MM')}${this.newLine()}`,
                size: EpsonSize.DOUBLE_WIDTH
            }, {
                text: `${this.getNonFiscalLabel('reprint_source')} ${sale.reprint_info.reprint_source}${this.newLine()}`,
                size: EpsonSize.DOUBLE_WIDTH
            }, {
                text: `${this.getNonFiscalLabel('reprint_new')} ${sale.reprint_info.reprint_new}${this.newLine()}`,
                size: EpsonSize.DOUBLE_WIDTH
            }
            );
        }

        for (const row of headerData) {
            builder.addText(row.text, {
                size: row.size,
                bold: row.bold || false,
                align: row.align || EpsonAlign.LEFT,
                reverse: row.reversed || false
            });
        }
    };

    private getItemIngredients(item: (VariationSaleItemWithPrice | SaleItemWithInfo)) {
        const result: string[] = [];
        const addedOrRemoved = groupBy(item.ingredients || [], i => i.type)

        for (const [addremType, ingredients] of Object.entries(addedOrRemoved)) {
            const typeLabel = this.getNonFiscalLabel(addremType as SalesItemsIngredients.TypeEnum) || "";

            let ingredientsString = '';

            for (const ingredient of ingredients) {
                switch (addremType) {
                    case 'added':
                        ingredientsString += (`${typeLabel}${ingredient.quantity !== 1 ? (ingredient.quantity + "x") : ""} ${ingredient.name}\n`);
                        break;
                    case 'removed':
                        ingredientsString += (`${typeLabel} ${ingredient.name}\n`);
                        break;
                    default:
                        ingredientsString += (`${ingredient.name}\n`);
                        break;
                }
            }

            if (ingredientsString) {
                result.push(ingredientsString);
            }
        }

        return result;
    }

    private getItemVariations(item: (VariationSaleItemWithPrice | SaleItemWithInfo)) {
        const result: string[] = []

        for (const variation of item.variations || []) {
            const variationQuantity = (variation.quantity ?? 1) === 1 ? '' : `${variation.quantity}x `;
            result.push(`${variation.name}\t${variationQuantity}${variation.value}`);
        }

        return result;
    }

    private printItemVariationGroup(builder: PrintLayoutBuilder, itemGroup: VariationSaleItemWithPrice[], printer: OrderPrinterWithConfig, config: PrintItemGroupConfig) {
        const {
            itemSpacing,
            itemHideBorder,
            itemHideBrackets,
            addPrices,
            itemsAlign,
            mergeSameItems
        } = this.getLayoutConfig(printer);

        const {
            bold,
            itemFontSize,
            variationFontSize,
            padding,
        } = config;

        const paddingText = ' '.repeat(padding);

        const addItemRows = (items?: VariationSaleItemWithPrice[], showSign?: boolean, header?: string) => {
            const itemsData: ItemVariationGroupData[] = [];

            for (const item of items || []) {
                const priceTag = addPrices ? ` ${this.$filter('sclCurrency')(item.final_price)}` : '';

                const itemData: ItemVariationGroupData = {
                    is_menu_item: !!item.sale_item_parent_uuid,
                    name: (item.half_portion ? "1/2 " : "") + (item.order_name || item.name || '').toUpperCase() + priceTag,
                    quantity: item.quantity_difference || item.quantity,
                    quantityDifference: !!item.quantity_difference,
                    ingredients: [],
                    variations: [],
                    notes: '',
                    underLineIngredients: !!item.ingredients_differences,
                    underlineVariations: !!item.variations_differences,
                    underlineNotes: !!item.notes_difference,
                    uuid: item.uuid
                };

                // AGGIUNTE / RIMOZIONI
                itemData.ingredients.push(...this.getItemIngredients(item));

                // VARIATIONS
                itemData.variations.push(...this.getItemVariations(item));

                // NOTES
                itemData.notes = item.notes || '';

                itemsData.push(itemData);
            }

            //Group items by same information (excluding quantity)
            let finalItemsData: ItemVariationGroupData[] = [];

            if (mergeSameItems) {
                const itemsGroup = groupBy(itemsData, i => `${i.name}${i.ingredients}${i.variations}${i.notes}${i.quantityDifference}${i.underLineIngredients}${i.underlineVariations}${i.underlineNotes}${i.is_menu_item}`);

                for (const items of Object.values(itemsGroup)) {
                    const quantity = items.reduce((acc, item) => acc + item.quantity, 0);
                    const sampleItem = items[0]!;

                    if (quantity) {
                        finalItemsData.push({
                            ...sampleItem,
                            quantity: quantity,
                        });
                    } else {
                        finalItemsData.push(...items);
                    }
                }
            } else {
                finalItemsData = itemsData;
            }

            if (finalItemsData.length && header) {
                builder
                    .addTextLine(` ${header} `, {
                        align: EpsonAlign.CENTER,
                        size: itemFontSize,
                        reverse: true
                    })
                    .setStyle({ reverse: false });
            }

            for (const item of finalItemsData) {
                const finalPaddingText = item.is_menu_item ? `${paddingText}  ` : paddingText;
                const quantitySign = (showSign && item.quantityDifference && item.quantity > 0) ? "+" : "";
                const quantityString = itemHideBrackets ? `${quantitySign}${item.quantity}` : `[${quantitySign}${item.quantity}]`;

                builder.addTextLine(finalPaddingText + `${quantityString} ${item.name}`, {
                    align: itemsAlign as EpsonAlign,
                    size: item.is_menu_item ? variationFontSize : itemFontSize
                });

                // INGREDIENTS (ADDING/REMOVAL)
                for (const ingredientsBlock of item.ingredients) {
                    builder.setStyle({
                        align: EpsonAlign.LEFT,
                        size: variationFontSize,
                        underline: item.underLineIngredients
                    });

                    for (const ingredient of ingredientsBlock.split('\n')) {
                        builder.addTextLine(finalPaddingText + ingredient);
                    }

                    builder.setStyle({ underline: false });
                }

                for (const variation of item.variations) {
                    const [variationName, variationValue] = variation.split('\t');

                    if (!variationName || !variationValue) {
                        continue;
                    }

                    builder
                        .addText(finalPaddingText + `${variationName}: `, { align: EpsonAlign.LEFT, size: variationFontSize })
                        .addText(variationValue, { underline: item.underlineVariations })
                        .setStyle({ underline: false })
                        .newLine();
                }

                // NOTES
                if (item.notes) {
                    builder
                        .addText(finalPaddingText + `${this.getNonFiscalLabel('notes')}: `, { align: EpsonAlign.LEFT, size: variationFontSize })
                        .addText(item.notes, { underline: item.underlineNotes })
                        .setStyle({ underline: false })
                        .newLine();
                }

                if (!itemHideBorder) {
                    builder.addText(this.border(printer.columns, '-'), {
                        size: EpsonSize.DOUBLE_WIDTH,
                        align: EpsonAlign.LEFT
                    });
                }

                builder.newLine(itemSpacing);
            }
        };

        // PRODOTTI E VARIANTI
        builder.setStyle({ bold: !!bold });

        const itemsByType = groupBy(itemGroup, group => group.type);

        addItemRows(itemsByType.removal, false, this.getNonFiscalLabel('item_removal'));
        addItemRows(itemsByType.addition, false, this.getNonFiscalLabel('item_addition'));

        if (itemsByType.edit) {
            const editedItems = groupBy(itemsByType.edit, (item) => item.quantity_difference ? 'quantity' : 'other');

            addItemRows(editedItems.quantity, true, this.getNonFiscalLabel('item_editquantity'));
            addItemRows(editedItems.other, false, this.getNonFiscalLabel('item_edit'));
        }

        builder.setStyle({ bold: false });
    }

    private buildVariation(variationOrder: PrintableVariation, printer: OrderPrinterWithConfig) {
        const builder = new PrintLayoutBuilder();

        const isOwnRoomOrder = this.isPrintersRoomOrder(printer, variationOrder);
        const exitNames = this.getExitNames();

        if (!isOwnRoomOrder) {
            return builder;
        }

        // Filter order_items which have category_id contained in printersIds
        const variationsToPrint = this.groupItemsByPrinterOwnership(variationOrder.variation_items, printer);

        // Check if empty
        if (!variationsToPrint.own) {
            return builder;
        }

        const {
            itemFontSize,
            itemsGroupBy,
            splitOrders,
            addPrices,
            otherPrintersFontSize,
            topSpace,
            bottomSpace,
            variationFontSize
        } = this.getLayoutConfig(printer);

        // Compile order prices if necessary
        if (addPrices) {
            const refSale = structuredClone({ sale_items: variationOrder.variation_items || [] });

            for (const item of refSale.sale_items) {
                item.quantity = item.quantity_difference || item.quantity;
            }

            this.saleUtilsService.calculateSalePrices(refSale as Sales);

            const orderItemsByUuid = keyBy(refSale.sale_items, oi => oi.uuid);

            for (const item of variationOrder.variation_items || []) {
                item.final_price = orderItemsByUuid[item.uuid]?.final_price || 0;
            }
        }

        const {
            groupedOwn: groupedVariations,
            groupedOther: otherInlineVariations,
            sortedGroupKeys: groups,
            lastGroupIndex
        } = this.prepareItemGroups(variationsToPrint.own, variationsToPrint.other_inline, itemsGroupBy);

        for (const [i, groupIndex] of groups.entries()) {
            const itemGroup = groupedVariations[groupIndex] || [];
            const otherInlineItemGroup = otherInlineVariations[groupIndex] || [];
            const hasOtherInlineItems = otherInlineItemGroup.length > 0;

            //Print header if it's the first group or the splitOrders is enabled
            if (i === 0 || splitOrders) {
                builder
                    .addText('', { size: EpsonSize.DOUBLE_WIDTH })
                    .addText(topSpace, { reverse: false, underline: false, bold: false, color: EpsonColor.COLOR_1 });

                this.buildOrderHeader(builder, printer, variationOrder, 'header');
            }

            if (groupIndex !== 0) {
                if (i > 0 && !splitOrders) {
                    builder.addText(this.border(printer.columns, '='), { align: EpsonAlign.LEFT });
                }

                let groupTitle;

                switch (itemsGroupBy) {
                    case 'exit':
                    case 'exit_and_category':
                        groupTitle = (exitNames[groupIndex] || `${this.getNonFiscalLabel('exit')} ${groupIndex}`).toUpperCase();
                        break;
                    default:
                        break;
                }

                if (groupTitle) {
                    builder
                        .addTextLine(groupTitle, { align: EpsonAlign.CENTER, size: itemFontSize })
                        .addText(this.border(printer.columns, '='), { align: EpsonAlign.LEFT, size: EpsonSize.DOUBLE_WIDTH });
                }
            } else {
                builder.newLine();
            }

            if (itemsGroupBy === 'exit_and_category') {
                const {
                    groupedOwn: ownGroups,
                    groupedOther: otherInlineGroups,
                    sortedGroupKeys: subGroups,
                } = this.prepareItemGroups(itemGroup, otherInlineItemGroup, 'category_id');

                for (const subGroupIndex of subGroups) {
                    const subItemGroup = ownGroups[subGroupIndex] || [];
                    const otherInlineGroup = otherInlineGroups[subGroupIndex] || [];

                    //Take category name from first item
                    const category = subItemGroup[0]?.category_name;

                    if (category) {
                        builder.addTextLine(category);
                    }

                    this.printItemVariationGroup(builder, subItemGroup, printer, { itemFontSize, variationFontSize, padding: 2, bold: hasOtherInlineItems });

                    if (hasOtherInlineItems) {
                        builder.addTextLine(this.border(printer.columns, '-'));
                        this.printItemVariationGroup(builder, otherInlineGroup, printer, { itemFontSize: otherPrintersFontSize, variationFontSize: otherPrintersFontSize, padding: 2 });
                    }
                }
            } else {
                this.printItemVariationGroup(builder, itemGroup, printer, { itemFontSize, variationFontSize, padding: 0, bold: hasOtherInlineItems });

                if (hasOtherInlineItems) {
                    builder.addTextLine(this.border(printer.columns, '-'));
                    this.printItemVariationGroup(builder, otherInlineItemGroup, printer, { itemFontSize: otherPrintersFontSize, variationFontSize: otherPrintersFontSize, padding: 0 });
                }
            }

            // Add bottom space
            builder.addText(bottomSpace);

            // Print other elements (printed only on the last group or when split_orders_by_exit is enabled)
            if ((i === lastGroupIndex || splitOrders) && variationsToPrint.other) {
                const addOtherItemRow = (item: VariationSaleItem, showSign: boolean) => {
                    builder.addTextLine(`[${((item.quantity_difference! > 0 && showSign) ? "+" : "")}${item.quantity_difference}]${(item.half_portion ? "1/2 " : "")} ${(item.order_name || item.name || '').toUpperCase()}`);
                };

                builder.setStyle({
                    align: EpsonAlign.RIGHT,
                    size: otherPrintersFontSize
                });

                const otherVariations = groupBy(variationsToPrint.other, this.groupItemsByGroupByPreference(itemsGroupBy));

                for (const [groupIndex, otherItemGroup] of Object.entries(otherVariations)) {
                    if (groupIndex != '0') {
                        switch (itemsGroupBy) {
                            case 'exit':
                                const exitName = exitNames[groupIndex] || `${this.getNonFiscalLabel('exit')} ${groupIndex}`;
                                builder.addTextLine(` - ${exitName.toUpperCase()} - `);
                                break;
                            default:
                                break;
                        }
                    }

                    // PRODOTTI E VARIANTI
                    const otherItemsByType = groupBy(otherItemGroup, item => item.type);

                    if (otherItemsByType.removal) {
                        builder.addTextLine(` ${this.getNonFiscalLabel('item_removal')} `);

                        for (const item of otherItemsByType.removal) {
                            addOtherItemRow(item, false);
                        }
                    }

                    if (otherItemsByType.addition) {
                        builder.addTextLine(` ${this.getNonFiscalLabel('item_addition')} `);

                        for (const item of otherItemsByType.addition) {
                            addOtherItemRow(item, false);
                        }
                    }

                    if (otherItemsByType.edit) {
                        const otherEdits = groupBy(otherItemsByType.edit, (item) => item.quantity_difference ? 'quantity' : 'other');

                        if (otherEdits.quantity) {
                            builder.addTextLine(` ${this.getNonFiscalLabel('item_editquantity')} `);

                            for (const item of otherEdits.quantity) {
                                addOtherItemRow(item, true);
                            }
                        }

                        if (otherEdits.other) {
                            builder.addTextLine(` ${this.getNonFiscalLabel('item_edit')} `);

                            for (const item of otherEdits.other) {
                                addOtherItemRow(item, true);
                            }
                        }
                    }
                }
            }

            // Print footer and cut if it's the last group or when split_orders_by_exit is enabled
            if (i === lastGroupIndex || splitOrders) {
                this.buildOrderHeader(builder, printer, variationOrder, 'footer');

                builder
                    .addText(bottomSpace)
                    .cut();
            }
        }

        if (printer.enable_buzzer) {
            builder.addPulse();
        }

        return builder;
    }

    private printItemGroup(builder: PrintLayoutBuilder, itemGroup: SaleItemWithInfo[], printer: OrderPrinterWithConfig, config: PrintItemGroupConfig, replacementInfo?: PrintInfo['replacement_info']) {
        const {
            itemSpacing,
            itemHideBorder,
            itemHideBrackets,
            itemsAlign,
            addPrices,
            mergeSameItems
        } = this.getLayoutConfig(printer);

        const {
            itemFontSize,
            variationFontSize,
            padding,
            bold
        } = config;

        const paddingText = ' '.repeat(padding);

        builder.setStyle({ bold: !!bold });

        const itemsData: ItemGroupData[] = [];

        // PRODOTTI E VARIANTI
        for (const item of itemGroup) {
            const priceTag = addPrices ? ` ${this.$filter('sclCurrency')(item.final_price)}` : '';

            const itemData: ItemGroupData = {
                quantity: item.quantity,
                is_menu_item: !!item.sale_item_parent_uuid,
                name: (item.half_portion ? "1/2 " : "") + (item.order_name || item.name || '').toUpperCase() + priceTag,
                ingredients: [],
                variations: [],
                notes: '',
                uuid: item.uuid
            };

            // AGGIUNTE / RIMOZIONI
            itemData.ingredients.push(...this.getItemIngredients(item));

            // VARIATIONS
            itemData.variations.push(...this.getItemVariations(item));

            // NOTES
            itemData.notes = item.notes || '';

            itemsData.push(itemData);
        }

        let finalItemsData: ItemGroupData[] = [];

        //Group items by same information (excluding quantity)
        if (mergeSameItems) {
            const itemsGroup = groupBy(itemsData, i => `${i.name}${i.ingredients}${i.variations}${i.notes}${i.is_menu_item}`);

            for (const items of Object.values(itemsGroup)) {
                const sampleItem = items[0]!;

                finalItemsData.push({
                    ...sampleItem,
                    quantity: items.reduce((acc, item) => acc + item.quantity, 0)
                });
            }
        } else {
            finalItemsData = itemsData;
        }

        const printerCols = printer.columns || 48;

        for (const item of finalItemsData) {
            const finalPaddingText = item.is_menu_item ? `${paddingText}  ` : paddingText;
            const quantity = itemHideBrackets ? `${item.quantity} ` : `[${item.quantity}] `;

            let itemBold = !!bold;

            // Retrieve the font size for the current item
            let fontSize = item.is_menu_item
                ? variationFontSize
                : itemFontSize;

            let finalText = finalPaddingText + quantity + item.name;

            if (replacementInfo?.is_replacement) {
                const isChanged = replacementInfo.changed_uuids.has(item.uuid);                

                if (isChanged) {
                    itemBold = true;

                    fontSize |= EpsonSize.DOUBLE_WIDTH;
                } else {
                    itemBold = false;

                    fontSize &= ~EpsonSize.DOUBLE_WIDTH;

                    // Check if double width is active via the EpsonSize flag
                    const isDoubleWidth = !!(fontSize & EpsonSize.DOUBLE_WIDTH);

                    // Calculate the limit of effective columns per row
                    const limit = isDoubleWidth
                        ? Math.floor(printerCols / 2)
                        : printerCols;

                    // Base text without the asterisk (keep at least a space between the name and the asterisks)
                    finalText = truncateString(finalText, limit - 3);

                    // Calculate spaces needed
                    const spacesNeeded = Math.max(0, limit - finalText.length - 2);

                    // The suffix will contain the necessary spaces followed by the asterisk
                    finalText += ' '.repeat(spacesNeeded) + '**';
                }
            }

            builder
                .addText(finalText, {
                    align: <EpsonAlign>itemsAlign,
                    size: fontSize,
                    bold: itemBold
                })
                .newLine(itemSpacing + 1);

            for (const ingredientsBlock of item.ingredients) {
                builder.setStyle({
                    align: EpsonAlign.LEFT,
                    size: variationFontSize
                });

                for (const ingredient of ingredientsBlock.split('\n')) {
                    builder.addTextLine(finalPaddingText + ingredient);
                }
            }

            for (const variation of item.variations) {
                const [variationName, variationValue] = variation.split('\t');

                builder.addTextLine(finalPaddingText + `${variationName}: ${variationValue}`, {
                    align: EpsonAlign.LEFT,
                    size: variationFontSize
                });
            }

            if (item.notes) {
                builder.addTextLine(finalPaddingText + `${this.getNonFiscalLabel('notes')}: ${item.notes}`, {
                    align: EpsonAlign.LEFT,
                    size: variationFontSize
                });
            }

            builder.setStyle({ size: EpsonSize.DOUBLE_WIDTH });

            if (!itemHideBorder && item !== finalItemsData[finalItemsData.length - 1]) {
                builder.addText(this.border(printer.columns, '-'), {
                    align: EpsonAlign.LEFT
                });
            }
        }

        builder.setStyle({ bold: false });
    }

    private buildTableVariation(variation: TableVariation, sale: PrintableSale, printer: OrderPrinterWithConfig) {
        const builder = new PrintLayoutBuilder();

        // Filter order_items which have category_id contained in printer.categories
        const itemsToPrint = this.groupItemsByPrinterOwnership(sale.sale_items || [], printer);

        // Check if empty
        if (!itemsToPrint.own) {
            return builder;
        }

        // Check if previous and current tables are the same, if so return EMPTY_ORDER
        if (variation.previous.table_id === variation.current.table_id) {
            return builder;
        }

        builder
            // Header
            .setStyle({ align: EpsonAlign.CENTER, size: (EpsonSize.DOUBLE_WIDTH | EpsonSize.DOUBLE_HEIGHT) })
            .addText(this.border(printer.columns, '='))
            .addTextLine(this.getNonFiscalLabel('table_edit'))
            .addTextLine(this.border(printer.columns, '='))
            // Previous table
            .addTextLine(this.getNonFiscalLabel('previous_table'), { align: EpsonAlign.LEFT, size: EpsonSize.DOUBLE_WIDTH })
            .addTextLine((variation.previous.table_id ? `${variation.previous.room_name} - ${variation.previous.table_name}` : `${this.getNonFiscalLabel('no_table')}`), { align: EpsonAlign.CENTER, size: (EpsonSize.DOUBLE_WIDTH | EpsonSize.DOUBLE_HEIGHT) }, 2)
            .setStyle({ align: EpsonAlign.LEFT, size: EpsonSize.DOUBLE_WIDTH })
            .addTextLine(this.border(printer.columns, '-'))
            // Current table
            .addTextLine(this.getNonFiscalLabel('current_table'))
            .addTextLine((variation.current.table_id ? `${variation.current.room_name} - ${variation.current.table_name}` : `${this.getNonFiscalLabel('no_table')}`), { align: EpsonAlign.CENTER, size: (EpsonSize.DOUBLE_WIDTH | EpsonSize.DOUBLE_HEIGHT) })
            // Cut
            .cut();

        return builder;
    }

    private getExitNames() {
        let exitNames: Record<string, string> = {};

        try {
            const exitNamesPref = this.configurationManagerService.getPreference('orders.exits_names');

            if (exitNamesPref) {
                exitNames = JSON.parse(exitNamesPref);
            }
        } catch (err) {
            console.error(err);
        }

        return exitNames;
    }

    public buildOrderStub(sale: Sales, docPrinter: Printers) {
        const printer = <DocumentPrinterWithConfig>this.printerUtilsService.getPrinterWithConfig(docPrinter);
        const printDate = moment();

        const printerCategories = keyBy(printer.categories || [], (c) => c.id);

        const addHeader = (builder: PrintLayoutBuilder) => {
            //Date header
            builder.addTextLine(printDate.format('DD/MM/YYYY HH:mm'), { size: 0, bold: false }, 2);

            //Customer number (if queue is enabled) and category
            const queueMode = this.configurationManagerService.getPreference('cashregister.queue.mode');

            if (queueMode && ['auto', 'manual'].includes(queueMode)) {
                builder
                    .addText(this.getNonFiscalLabel('customer_stub'), { size: EpsonSize.DOUBLE_HEIGHT | EpsonSize.DOUBLE_WIDTH, bold: false })
                    .addText(` ${sale.sale_number} `, { size: EpsonSize.DOUBLE_HEIGHT | EpsonSize.DOUBLE_WIDTH, bold: true })
            }
        }

        const categoriesArr = Object.values(printerCategories);
        const builder = new PrintLayoutBuilder();

        const getSaleItemsLines = (category?: Categories) => {
            const lines: { text: string, bold: boolean, size: EpsonSize }[] = [];

            for (const saleItem of sale.sale_items || []) {
                const isPrinterOwnCategory = saleItem.category_id && !!printerCategories[saleItem.category_id];

                if (!isPrinterOwnCategory) {
                    continue;
                }

                const isThisCategory = !category || category.id === saleItem.category_id;

                if (isThisCategory || printer.configuration.print_other_categories) {
                    lines.push({ text: `${saleItem.quantity.toString().padEnd(4, ' ')} ${saleItem.name}`, bold: isThisCategory, size: isThisCategory ? EpsonSize.DOUBLE_HEIGHT : EpsonSize.NORMAL });

                    const ingredients = this.getItemIngredients(saleItem).flatMap((block) => block.split('\n')).filter(Boolean);
                    const variations = this.getItemVariations(saleItem).map((block) => <[string, string]>block.split('\t'));
                    const padding = ' '.repeat(4);

                    for (const ingredient of ingredients) {
                        lines.push({ text: padding + ingredient, bold: false, size: EpsonSize.NORMAL });
                    }

                    for (const [variationName, variationValue] of variations) {
                        lines.push({ text: padding + `${variationName}: ${variationValue}`, bold: false, size: EpsonSize.NORMAL });
                    }

                    if (saleItem.notes) {
                        lines.push({ text: padding + `${this.getNonFiscalLabel('notes')}: ${saleItem.notes}`, bold: false, size: EpsonSize.NORMAL });
                    }
                }
            }

            return lines;
        }

        if (printer.configuration.split_stubs_by_category) {
            for (const category of categoriesArr) {
                const itemLines = getSaleItemsLines(category);

                if (!itemLines.length) {
                    continue;
                }

                addHeader(builder);

                // Category
                builder.addTextLine(category.name, { size: EpsonSize.DOUBLE_HEIGHT, bold: false }, 2);
                builder.addText(this.border(printer.columns, '-'), { size: 0, bold: false });

                for (const line of itemLines) {
                    const { text, ...style } = line;

                    builder.addTextLine(text, style);
                }

                builder.newLine(2);
                builder.cut();
            }
        } else {
            const itemLines = getSaleItemsLines();

            if (!itemLines.length) {
                return builder;
            }

            addHeader(builder);
            builder.addTextLine('', undefined, 2);

            for (const line of itemLines) {
                const { text, ...style } = line;

                builder.addTextLine(text, style);
            }

            builder.newLine(2);
            builder.cut();
        }


        return builder;
    }

    private buildOrder(sale: PrintableSale, printer: OrderPrinterWithConfig) {
        const builder = new PrintLayoutBuilder();

        const isOwnRoomOrder = this.isPrintersRoomOrder(printer, sale);
        const skipContent = (!!sale.goExit && !!this.configuration.goExit_skip_content);

        const exitNames = this.getExitNames();

        if (!isOwnRoomOrder) {
            return builder;
        }

        const {
            itemsGroupBy,
            topSpace,
            bottomSpace,
            splitOrders,
            itemFontSize,
            addPrices,
            variationFontSize,
            otherPrintersFontSize,
        } = this.getLayoutConfig(printer);

        // Compile order prices if necessary
        if (addPrices) {
            this.saleUtilsService.calculateSalePrices(sale);
        }

        // Filter order_items which have category_id contained in printer.categories
        const itemsToPrint = this.groupItemsByPrinterOwnership(sale.sale_items || [], printer);

        // Check if empty
        if (!itemsToPrint.own) {
            return builder;
        }

        const {
            groupedOwn: groupedItems,
            groupedOther: otherInlineGroupedItems,
            sortedGroupKeys: groups,
            lastGroupIndex
        } = this.prepareItemGroups(itemsToPrint.own!, itemsToPrint.other_inline, itemsGroupBy);

        for (const [i, groupIndex] of groups.entries()) {
            const itemGroup = groupedItems[groupIndex] || [];
            const otherInlineGroup = otherInlineGroupedItems[groupIndex] || [];
            const hasOtherInlineItems = otherInlineGroup.length > 0;

            //Print header if it's the first group or the splitOrders is enabled
            if (i === 0 || splitOrders) {
                builder
                    .setStyle({
                        reverse: false,
                        underline: false,
                        bold: false,
                        color: EpsonColor.COLOR_1
                    })
                    .addText(topSpace);

                this.buildOrderHeader(builder, printer, sale, 'header');
            }

            if(sale.replacement_info?.is_replacement) {
                builder.addTextLine(this.getNonFiscalLabel('reprint_order'), {
                    align: EpsonAlign.CENTER,
                    size: EpsonSize.DOUBLE_HEIGHT | EpsonSize.DOUBLE_WIDTH,
                    reverse: true
                }).setStyle({ reverse: false });
            }

            if (sale.goExit) { // Go exit
                let goExitLabel = '';

                switch (itemsGroupBy) {
                    case 'exit':
                        goExitLabel = exitNames[groupIndex]
                            ? this.getNonFiscalLabel('go_exit_name', { exitName: exitNames[groupIndex].toUpperCase() })
                            : `${this.getNonFiscalLabel('go_exit')} ${groupIndex}`;
                        break;
                    default:
                        if (i === 0) {
                            goExitLabel = `${this.getNonFiscalLabel('go_exit')}\n`;
                        }
                        break;
                }

                builder
                    .addTextLine(goExitLabel, { align: EpsonAlign.CENTER, size: itemFontSize })
                    .addTextLine(this.border(printer.columns, '='), { align: EpsonAlign.LEFT, size: EpsonSize.DOUBLE_WIDTH });
            } else { // Normal order send
                if (groupIndex !== 0) {
                    if (i > 0 && !splitOrders) { //Add upper borders
                        builder.addText(this.border(printer.columns, '='), { align: EpsonAlign.LEFT, size: EpsonSize.DOUBLE_WIDTH });
                    }

                    let groupTitle;

                    switch (itemsGroupBy) {
                        case 'exit':
                        case 'exit_and_category':
                            groupTitle = (exitNames[groupIndex] || `${this.getNonFiscalLabel('exit')} ${groupIndex}`).toUpperCase();
                            break;
                        default:
                            break;
                    }

                    if (groupTitle) {
                        builder
                            .addTextLine(groupTitle, { align: EpsonAlign.CENTER, size: itemFontSize })
                            .addText(this.border(printer.columns, '='), { align: EpsonAlign.LEFT, size: EpsonSize.DOUBLE_WIDTH });
                    }
                } else {
                    builder.newLine();
                }
            }

            if (!skipContent) {
                if (itemsGroupBy === 'exit_and_category') {
                    const {
                        groupedOwn: ownSubGroups,
                        groupedOther: inlineSubGroups,
                        sortedGroupKeys: subGroups,
                    } = this.prepareItemGroups(itemGroup, otherInlineGroup, 'category_id');

                    for (const subGroupIndex of subGroups) {
                        const subItemGroup = ownSubGroups[subGroupIndex] || [];
                        const otherInlineGroup = inlineSubGroups[subGroupIndex] || [];

                        //Take category name from first item
                        const category = subItemGroup[0]?.category_name;

                        if (category) {
                            builder.addTextLine(category);
                        }

                        this.printItemGroup(builder, subItemGroup, printer, { itemFontSize, variationFontSize, padding: 2, bold: hasOtherInlineItems }, sale.replacement_info);

                        if (otherInlineGroup.length) {
                            builder.addTextLine(this.border(printer.columns, '-'));
                            this.printItemGroup(builder, otherInlineGroup, printer, { itemFontSize: otherPrintersFontSize, variationFontSize: otherPrintersFontSize, padding: 2 }, sale.replacement_info);
                        }
                    }
                } else {
                    this.printItemGroup(builder, itemGroup, printer, { itemFontSize, variationFontSize, padding: 0, bold: hasOtherInlineItems }, sale.replacement_info);

                    if (hasOtherInlineItems) {
                        builder.addTextLine(this.border(printer.columns, '-'));
                        this.printItemGroup(builder, otherInlineGroup, printer, { itemFontSize: otherPrintersFontSize, variationFontSize: otherPrintersFontSize, padding: 0 }, sale.replacement_info);
                    }
                }

                // Add bottom space
                builder.addText(bottomSpace);

                // Print other elements if there are (printed only on the last group or when split_orders_by_exit is enabled)
                if ((i === lastGroupIndex || splitOrders) && itemsToPrint.other) {
                    const otherItems = groupBy(itemsToPrint.other, this.groupItemsByGroupByPreference(itemsGroupBy));

                    for (const [groupIndex, otherItemGroup] of Object.entries(otherItems)) {
                        builder.setStyle({
                            align: EpsonAlign.RIGHT,
                            size: otherPrintersFontSize
                        });

                        // USCITA
                        if (groupIndex != '0') {
                            switch (itemsGroupBy) {
                                case 'exit':
                                    const exitName = exitNames[groupIndex] || `${this.getNonFiscalLabel('exit')} ${groupIndex}`;
                                    builder.addTextLine(` - ${exitName.toUpperCase()} - `)
                                    break;
                                default:
                                    break;
                            }
                        }

                        // PRODOTTI E VARIANTI
                        for (const item of otherItemGroup) {
                            builder.addTextLine(`${item.quantity} x ${item.half_portion ? '1/2 ' : ''}${(item.order_name || item.name || '').toUpperCase()}`);
                        }
                    }
                }
            }

            // Print footer and cut if it's the last group or when split_orders_by_exit is enabled
            if (i === lastGroupIndex || splitOrders) {
                if(sale.replacement_info?.is_replacement) {
                    builder.addTextLine(this.getNonFiscalLabel('reprint_order'), {
                        align: EpsonAlign.CENTER,
                        size: EpsonSize.DOUBLE_HEIGHT | EpsonSize.DOUBLE_WIDTH,
                        reverse: true
                    }).setStyle({ reverse: false });
                }

                this.buildOrderHeader(builder, printer, sale, 'footer');
                builder.cut();
            }
        }

        if (printer.enable_buzzer) {
            builder.addPulse();
        }

        return builder;
    }

    private async buildNonFiscalSale(printerDriver: NFThermalPrinter, sale: SaleWithOptions, printer: OrderPrinterWithConfig) {
        const numCols = printer.columns || 48;

        // Get non-fiscal document lines
        const documentLines = this.$injector.get("salePrintingUtils").saleToNonFiscalDocument(sale, {
            printerColumns: numCols,
            tail: sale.options?.tail
        });

        const builder = new PrintLayoutBuilder();

        // Add logo and set initial printer settings
        builder
            .addLogo()
            .setStyle({
                bold: false,
                color: EpsonColor.COLOR_1,
                reverse: false,
                underline: false,
                size: 0,
            });

        // Add shop header if present
        if (sale.options?.header) {
            this.buildShopHeader(builder, sale.options.header);
        }

        // Process and print document lines
        for (const line of documentLines) {
            switch (line.type) {
                case 'text':
                    builder.addText(line.content, {
                        align: line.align || EpsonAlign.LEFT,
                        bold: !!line.bold,
                        size: line.doubleHeight ? 1 : 0
                    });
                    break;
                case 'qrcode':
                    builder
                        .setAlign(line.align || EpsonAlign.CENTER)
                        .addQRCode(line.content, line.size);
                    break;
            }
        }

        // Strooka table integration
        await this.addStrookaCode(builder, sale, printerDriver instanceof EPosDriver).catch(() => { });

        builder.cut();

        return builder;
    }

    private async addStrookaCode(builder: PrintLayoutBuilder, sale: SaleWithOptions, addQRCode: boolean) { 
        const strookaKey = this.configurationManagerService.getPreference('orders.strooka_shared_key');

        if (!sale.table_id || !strookaKey) {
            return;
        }

        let strookaApiUrl = this.configurationManagerService.getPreference('orders.strooka_api_url') || "https://api.strooka.com/plugin/scloby/tables/open/";

        if (!strookaApiUrl.endsWith("/")) {
            strookaApiUrl += "/";
        }

        const result = await this.$http({
            url: `${strookaApiUrl}${sale.table_id}/`,
            method: "POST",
            headers: { 'Shared-Key': strookaKey },
            data: {
                covers: sale.covers || 1,
                order_id: sale.id
            }
        });

        if(!result.data?.url) {
            return;
        }

        const strookaUrl = _.unescape(result.data.url);

        builder.setSize(2, 2);
        let headerText = this.configurationManagerService.getPreference('orders.strooka_header_text');

        if (!headerText) {
            if (addQRCode) {
                headerText = "Inquadra con il telefono il QRcode";
            }
        }

        if (headerText) {
            builder
                .newLine(2)
                .addTextLine(headerText)
        }

        builder.addQRCode(strookaUrl, 8);

        const strookaDomain = strookaUrl.match(/(^.*.)[\/][?]/)?.[1] || '';

        const accessText = addQRCode
            ? "In alternativa accedi su"
            : "Accedi su";

        builder
            .addTextLine(accessText)
            .addTextLine(strookaDomain)
            .addTextLine("ed inserisci il codice")
            .setSize(3, 3)
            .addText(result.data.pin)
            .setSize(1, 1);
    }

    private async printOrder(payload: PrintableSaleOrVariation | SaleWithOptions | TableVariation, originalPayload: VariationSale | Sales, printer: OrderPrinterWithConfig) {
        this.logEvent(`Printing order on printer: [${printer.name}]`);

        switch (printer.type) {
            case 'nonfiscal':
            case 'receipt':
                const driverInfo = this.getDriverInfo(printer);

                if (!driverInfo?.driverObj) {
                    return 'INVALID_DRIVER';
                }

                const printerDriver = new driverInfo.driverObj(printer.ip_address!, driverInfo.port, driverInfo.configuration);

                let documentBuilder: PrintLayoutBuilder | undefined;

                switch (payload.print_type) {
                    case 'order':
                        documentBuilder = 'variation_items' in payload
                            ? this.buildVariation(payload, printer)
                            : this.buildOrder(payload, printer);
                        break;
                    case 'sale_nonfiscal':
                        documentBuilder = await this.buildNonFiscalSale(printerDriver, payload, printer);
                        break;
                    case 'table_variation':
                        documentBuilder = this.buildTableVariation(payload, <PrintableSale>originalPayload, printer);
                        break;
                    default:
                        break;
                }

                if(!documentBuilder || documentBuilder.isEmpty()) {
                    return 'EMPTY_ORDER';
                }

                return documentBuilder.executeInstructionsOnDriver(printerDriver);
            case 'kds':
            /*
                if (['sale'].includes(payload.print_type)) {
                    return 'EMPTY_ORDER';
                }

                return this.KDSManager.sendOrders(printer, [originalPayload]).then(
                    () => 'PRINTED',
                    () => 'CONNECTION_ERROR'
                );
            */
            default:
                break;
        }

        return 'CONNECTION_ERROR';
    }

    private async compileOrderInfo(sale: PrintableSaleOrVariation, originalPayload: Sales, printers: OrderPrinterWithConfig[]) {
        const isVariation = 'variation_items' in sale;

        const coverConfig = await this.saleUtilsService.getCoverConfiguration(originalPayload);

        if (coverConfig.type === 'item') {
            if (isVariation) {
                sale.variation_items = sale.variation_items.filter(item => item.item_id !== coverConfig.data.id);
            } else {
                sale.sale_items = sale.sale_items.filter(item => item.item_id !== coverConfig.data.id);
            }
        }

        const orderItems = (isVariation ? sale.variation_items : sale.sale_items) || [];
        const itemIds = Array.from(new Set(orderItems.map(item => item.item_id))).filter(id => id) as number[];

        //Set final amount
        sale.final_amount = 'final_amount' in originalPayload ? originalPayload.final_amount : originalPayload.amount;

        //Set up channel info if present
        if ('channel' in sale && sale.channel && sale.channel !== 'pos') {
            try {
                const channel = await this.entityManagerService.channels.fetchOneOffline(sale.channel);

                if (channel) {
                    sale.channel = channel.name;
                }
            } catch (err) {
                this.errorsLogger.err("[NonFiscalDriver] Error while getting channel info for order");
            }
        }

        //Prepare category-to-printers map
        const categoryToPrinters: Record<number, Record<number, boolean>> = {};

        for (const printer of printers) {
            for (const category of printer.categories || []) {
                const catId = category.id!;

                if (!categoryToPrinters[catId]) {
                    categoryToPrinters[catId] = {};
                }

                categoryToPrinters[catId][printer.id!] = true;
            }
        }

        const itemsMap = await this.entityManagerService.items.fetchMapFromIdsOffline(itemIds);

        //Prepare item-to-printers map
        const itemToPrinters: Record<number, Record<number, boolean>> = {};

        for (const itemId of itemIds) {
            const item = itemsMap[itemId];

            if (item) {
                for (const printer of item.printers || []) {
                    const itemId = item.id!;

                    if (!itemToPrinters[itemId]) {
                        itemToPrinters[itemId] = {};
                    }

                    itemToPrinters[itemId][printer.id!] = true;
                }
            }
        }

        const addIncludedIngredients = this.configurationManagerService.getShopPreference('nofiscalprinter.print_included_ingredients');

        //Apply maps to order items
        for (const orderItem of orderItems) {
            if (!orderItem.exit) {
                orderItem.exit = 0;
            }

            //Apply missing info (e.g: order_name, category_name) to items
            if (orderItem.item_id) {
                const item = itemsMap[orderItem.item_id];

                if (item) {
                    orderItem.order_name = item.order_name;

                    if (orderItem.category_id) {
                        orderItem.category_name = item.category?.name;
                    }

                    if (addIncludedIngredients) {
                        const componentsToAdd: SalesItemsIngredients[] = [];

                        for (const component of (item.components || [])) {
                            componentsToAdd.push({
                                name: component.name,
                                ingredient_id: component.id,
                                price_difference: component.price_difference,
                                quantity: 1,
                            });
                        }

                        //Merge included ingredients with sale item changes
                        const ingredientsById = groupBy([...componentsToAdd, ...(orderItem.ingredients || [])], i => i.ingredient_id);
                        const finalIngredients: SalesItemsIngredients[] = [];

                        for (const ingredients of Object.values(ingredientsById)) {
                            const finalQuantity = ingredients.reduce((acc, i) => acc + (i.type === 'removed' ? -(i.quantity || 0) : (i.quantity || 0)), 0);

                            if (finalQuantity === 0) {
                                continue;
                            }

                            const sampleIngredient = ingredients[0]!;

                            finalIngredients.push({
                                ...sampleIngredient,
                                quantity: finalQuantity,
                                type: finalQuantity > 0 ? 'added' : 'removed'
                            });
                        }

                        orderItem.ingredients = finalIngredients;
                    }
                }
            }

            const itemPrinters: Record<number, boolean> = {};

            if (orderItem.category_id) {
                Object.assign(itemPrinters, categoryToPrinters[orderItem.category_id]);
            }

            if (orderItem.item_id) {
                Object.assign(itemPrinters, itemToPrinters[orderItem.item_id]);
            }

            Object.assign(orderItem, { printers: itemPrinters });
        }
    }

    private async sendOrderToPrinters(order: PrintableSaleOrVariation | TableVariation, originalPayload: Sales) {
        const printers = this.printers;
        const printAttempts = parseInt(this.configurationManagerService.getPreference('orders.print_attempts') || '1') || 1;

        const results: { printer: OrderPrinterWithConfig, result: NFSendResult }[] = [];

        if (order.print_type === 'table_variation') {
            await this.compileOrderInfo(<PrintableSale>originalPayload, originalPayload, printers);
        } else {
            await this.compileOrderInfo(order, originalPayload, printers);
        }

        //Send orders to 5 different printers in parallel, if there are printers with the same IP do them in series
        const printersByIP = groupBy(printers, p => p.ip_address);

        await asyncParallelLimit(Object.values(printersByIP), async (printerSet) => {
            for (const printer of printerSet) {
                let attempts = 0;
                let printResult;

                do {
                    attempts++;
                    printResult = await this.printOrder(order, originalPayload, printer);
                } while (!['PRINTED', 'EMPTY_ORDER'].includes(printResult) && attempts < printAttempts);

                results.push({
                    printer: printer,
                    result: printResult
                });

                if (attempts > 1) {
                    this.errorsLogger.sendReport({
                        type: 'printOrders',
                        content: `Printer [${printer.id}] [${printer.ip_address}] returned ${printResult} after ${(attempts + 1)} attempts`
                    });
                }
            }
        }, { parallelLimit: 5 });

        return results;
    }

    public setup(printers: Printers[], options?: SetupOptions) {
        this.printers = structuredClone(printers).map(printer => <OrderPrinterWithConfig>this.printerUtilsService.getPrinterWithConfig(printer));

        this.configuration.goExit_skip_content = !!(options?.goExit_skip_content);

        return structuredClone(this.printers);
    }

    public async discoverPrinters(driver: string) {
        try {
            const printersInt: number[] = [];

            const driverInfo = this.getDriverInfo({
                driver: driver,
                name: '',
                type: 'nonfiscal',
                configuration: {}
            });

            if (!driverInfo) {
                throw 'INVALID_DRIVER';
            }

            const netInfo = await this.environmentInfoService.getNetworkInfo();

            if (!this.validateIPaddress(netInfo.client_lan_ip)) {
                throw 'INVALID_IP_ADDRESS'
            }

            const subnetInfo = IpSubnetCalculator.calculateCIDRPrefix(netInfo.client_lan_ip, netInfo.client_lan_subnet);

            if (!subnetInfo) {
                throw 'INVALID_IP_ADDRESS';
            }

            const ipLow = subnetInfo.ipLow;
            const ipHigh = subnetInfo.ipHigh;

            this.logEvent(`Start scanning (${this.intToIP(ipLow)} - ${this.intToIP(ipHigh)})...`);

            const ipToScan: number[] = [];

            for (let ip = ipLow + 1; ip < ipHigh; ip++) {
                ipToScan.push(ip);
            }

            await asyncParallelLimit(ipToScan, async (ip) => {
                const result = await driverInfo.driverObj.connectAttempt(this.intToIP(ip), driverInfo.port);

                if (result.connected) {
                    printersInt.push(ip);
                }
            }, {
                parallelLimit: 32
            });

            const printers = printersInt.sort().map(this.intToIP);

            this.logEvent(`Printers found:`, printers);

            return printers;
        } catch (err: any) {
            return [];
        }
    }

    public smartPrintSale(sale: Sales, reprint: boolean) {
        const { sale_items, ...clonedSale } = structuredClone(sale);

        return this.sendOrderToPrinters({
            ...clonedSale,
            sale_items: sale_items || [],
            reprint: reprint,
            print_type: 'order'
        }, structuredClone(sale));
    }

    public async smartPrintVariationSale(saleVariation: VariationSale, sale: Sales) {
        const replacementMode = ['delivery', 'take_away'].includes(sale.order_type!) && this.configurationManagerService.getPreference('orders.print_replacement_order_for_delivery_takeaway');

        if (replacementMode) {
            // REPLACEMENT mode: print the whole sale instead of the variation
            const localSale = structuredClone(sale) as PrintableSale;
            localSale.print_type = 'order';

            // Identify the UUIDs of the items that have been added or modified in this variation
            const changedUuids = new Set((saleVariation.variation_items || [])
                .filter(v => v.type === 'addition' || v.type === 'edit')
                .map(v => v.uuid))

            localSale.replacement_info = {
                is_replacement: true,
                changed_uuids: changedUuids
            };

            return this.sendOrderToPrinters(localSale, sale);
        } else {
            // Standard behavior for other types (print deltas only)
            const localVariation = structuredClone(saleVariation) as PrintableVariation;
            localVariation.print_type = 'order';
            return this.sendOrderToPrinters(localVariation, sale);
        }
    }

    public printNonFiscalSale(sale: Sales, printer: Printers, options: PrintNonFiscalSaleOptions) {
        const localSale = structuredClone(sale) as SaleWithOptions
        localSale.options = structuredClone(options || {})
        localSale.print_type = 'sale_nonfiscal';

        return this.printOrder(localSale, sale, <OrderPrinterWithConfig>this.printerUtilsService.getPrinterWithConfig(printer));
    }

    public printSaleTableVariation(tableVariation: TableVariation, sale: Sales) {
        return this.sendOrderToPrinters(structuredClone(tableVariation), structuredClone(sale));
    }

    public smartGoExit(sale: Sales, exit: number) {
        const localSale = structuredClone(sale);

        return this.sendOrderToPrinters({
            ...localSale,
            sale_items: localSale.sale_items?.filter((saleItem) => saleItem.exit === exit) || [],
            print_type: 'order',
            goExit: true
        }, structuredClone(sale));
    }

    public async printDocument(saleToPrint: Sales, printerDocumentData: DocumentPrinterOptions, optionsPrint: any): Promise<[boolean, SalesDocuments[]]> {
        const printer = this.printers[0];

        if (!printer) {
            throw 'INVALID_PRINTER';
        }

        const driverInfo = this.getDriverInfo(printer);

        if (!driverInfo) {
            throw 'INVALID_DRIVER';
        }

        Object.assign(driverInfo.configuration, {
            eReceiptMode: !!(printerDocumentData?.options?.eReceipt)
        });

        const printerDriver = new driverInfo.driverObj(printer.ip_address!, driverInfo.port, driverInfo.configuration);

        const options: any = structuredClone(optionsPrint || {});
        options.columns = printer.columns;

        const documentData = await this.DocumentBuilder.buildDocument(printerDriver, saleToPrint, printerDocumentData, options);

        const result = await printerDriver.send();

        if (result !== 'PRINTED' && (!printer.fiscal_provider || optionsPrint.isReprint)) {
            throw result;
        }

        return [(result === 'PRINTED'), [{
            ...documentData,
            date: new Date(),
            printer_serial: undefined
        }]];
    }

    public async printFreeNonFiscal(documentToPrint: string | string[], options?: PrintFreeOptions) {
        const printer = this.printers[0];

        if (!printer) {
            throw 'INVALID_PRINTER';
        }

        const driverInfo = this.getDriverInfo(printer);

        if (!driverInfo) {
            throw 'INVALID_DRIVER';
        }

        const printerDriver = new driverInfo.driverObj(printer.ip_address!, driverInfo.port, driverInfo.configuration);

        if (typeof documentToPrint == 'string') {
            documentToPrint = documentToPrint.split('\n');
        }

        if (!Array.isArray(documentToPrint)) {
            throw 'INVALID_DOCUMENT';
        }

        const builder = new PrintLayoutBuilder();

        if (options?.printLogo) {
            builder.addLogo();
        }

        if (options?.printCopyHeader) {
            builder.addTextLine("RECEIPT COPY", {
                align: EpsonAlign.CENTER,
                size: 0
            }, 2);
        }

        if (options?.header) {
            this.buildShopHeader(builder, options?.header);

            builder.setStyle({
                align: EpsonAlign.LEFT,
                size: 0
            });
        }

        for (const line of documentToPrint) {
            builder.addTextLine(line);
        }

        if (typeof options?.barcode == 'object') {
            builder.setAlign(EpsonAlign.CENTER);

            switch (options.barcode.type) {
                case 'CODE39':
                case 'EAN13':
                default:
                    builder
                        .newLine()
                        .addBarcode(options.barcode.value, EpsonBarcode[options.barcode.type] || EpsonBarcode.CODE39, EpsonHRI.BELOW, EpsonFont.FONT_B, 2, 66)
                        .newLine()
                    break;
                case 'QRCODE':
                    builder
                        .newLine()
                        .addQRCode(options.barcode.value, 8)
                        .newLine();
                    break;
            }
        }

        builder.cut();

        const result = await builder.executeInstructionsOnDriver(printerDriver);

        if (result !== 'PRINTED') {
            throw 'CONNECTION_ERROR';
        }
    }

    public async printFreeDocumentLayout(documentToPrint: PrintLayoutBuilder, printer: Printers) {
        if (!printer) {
            throw 'INVALID_PRINTER';
        }

        const printerWithConfig = <OrderPrinterWithConfig>this.printerUtilsService.getPrinterWithConfig(printer);

        const driverInfo = this.getDriverInfo(printerWithConfig);

        if (!driverInfo) {
            throw 'INVALID_DRIVER';
        }

        const printerDriver = new driverInfo.driverObj(printer.ip_address!, driverInfo.port, driverInfo.configuration);

        const result = await documentToPrint.executeInstructionsOnDriver(printerDriver);

        if (result !== 'PRINTED') {
            throw 'CONNECTION_ERROR';
        }
    }

    public async isReachable() {
        const printer = this.printers[0];

        if (!printer) {
            throw 'INVALID_PRINTER';
        }

        const driverInfo = this.getDriverInfo(printer);

        if (!driverInfo) {
            throw 'INVALID_DRIVER';
        }

        const result = await driverInfo.driverObj.connectAttempt(printer.ip_address!, driverInfo.port);

        if (!result.connected) {
            throw 'CONNECTION_ERROR';
        }
    }

    public async openCashDrawer() {
        const printer = this.printers[0];

        if (!printer) {
            throw 'INVALID_PRINTER';
        }

        const driverInfo = this.getDriverInfo(printer);

        if (!driverInfo) {
            throw 'INVALID_DRIVER';
        }

        const printerDriver = new driverInfo.driverObj(printer.ip_address!, driverInfo.port, driverInfo.configuration);
        printerDriver.addPulse();

        const result = await printerDriver.send();

        if (result !== 'PRINTED') {
            throw 'CONNECTION_ERROR';
        }
    }

    public async displayText(printer: Printers, textLines: string[]) {
        const printerWithConfig = <OrderPrinterWithConfig>this.printerUtilsService.getPrinterWithConfig(printer)
        const driverInfo = this.getDriverInfo(printerWithConfig);

        if (!driverInfo) {
            throw 'INVALID_PRINTER';
        }

        if (typeof driverInfo.driverObj.displayText !== 'function') {
            throw 'METHOD_DOES_NOT_EXIST';
        }

        await driverInfo.driverObj.displayText(printerWithConfig, textLines);
    }
}

angular.module('printers').factory('NonFiscalDriver', NonFiscalDriver);